import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, X, Sparkles, Rocket, MapPin, Check } from "lucide-react";
import { SEO } from "@/components/SEO";
import { useTravelPersonality } from "@/hooks/useTravelPersonality";
import { ScrollProgress } from "@/components/landing/ScrollProgress";
import { ScrollReveal } from "@/components/landing/ScrollReveal";
import { SectionNav } from "@/components/landing/SectionNav";
import { cn } from "@/lib/utils";

import featureQuiz from "@/assets/feature-quiz.jpg";
import featureResults from "@/assets/feature-results.jpg";
import featureShortlist from "@/assets/feature-shortlist.jpg";
import featureShareDeck from "@/assets/feature-share-deck.jpg";
import featureItinerary from "@/assets/feature-itinerary.jpg";
import featureBooking from "@/assets/feature-booking.jpg";
import featureWorldmap from "@/assets/feature-worldmap.jpg";
import featurePersonality from "@/assets/feature-personality.jpg";

const Landing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const justSignedUp = (location.state as any)?.justSignedUp === true;
  const signedUpEmail = (location.state as any)?.email as string | undefined;
  const [showWelcome, setShowWelcome] = useState(justSignedUp);
  const { personality } = useTravelPersonality(false);
  const canFastTrack = !!personality && personality.archetype_confidence >= 30 && !!personality.archetype;

  useEffect(() => {
    if (justSignedUp) {
      window.history.replaceState({}, '');
    }
  }, [justSignedUp]);

  return (
    <div className="relative overflow-hidden">
      <ScrollProgress />
      <SectionNav />

      {showWelcome && (
        <div className="bg-highlight text-white">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm font-medium truncate">
                Welcome to TripVerse{signedUpEmail ? `, ${signedUpEmail}` : ''}. You're signed in, so your saves and trips will now sync to your account.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowWelcome(false)}
              aria-label="Dismiss welcome message"
              className="flex-shrink-0 rounded-full p-1 hover:bg-white/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <SEO title="AI-Powered Travel Destination Finder" description="Take a quick quiz and discover your perfect travel destination with real-time pricing." />

      {/* Hero */}
      <section id="hero" className="relative bg-primary overflow-hidden isolate min-h-[85svh] sm:min-h-[90vh] flex flex-col justify-center">
        {/* Ambient background blobs */}
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-accent/20 rounded-full mix-blend-screen blur-[128px] pointer-events-none" />
        <div className="absolute -bottom-48 -right-24 w-[500px] h-[500px] bg-highlight/10 rounded-full mix-blend-screen blur-[160px] pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-6 py-10 sm:py-20 relative z-10 flex-1 flex flex-col justify-center">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Content column */}
            <motion.div
              className="lg:col-span-7 flex flex-col items-start"
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/30 backdrop-blur-sm mb-6 sm:mb-8"
              >
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                <span className="text-xs font-semibold tracking-widest uppercase text-accent-foreground">AI-Powered Travel Decisions</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold text-primary-foreground leading-[0.95] tracking-tighter mb-6 sm:mb-8"
              >
                <span className="font-normal">Let's decide your</span>
                <br />
                <span className="font-black text-5xl sm:text-7xl md:text-8xl tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-primary-foreground via-primary-foreground to-accent">
                  next trip
                </span>
                <br />
                <span className="font-normal">in 2 minutes.</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="text-lg sm:text-xl text-primary-foreground/65 max-w-lg mb-8 sm:mb-10 font-light leading-relaxed"
              >
                Answer a few questions. Skip the overwhelm. Find your next trip in 2 mins.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto"
              >
                <Button
                  variant="hero"
                  size="lg"
                  onClick={() => navigate('/quiz')}
                  className="text-base sm:text-lg px-8 sm:px-10 py-5 sm:py-6 h-auto group rounded-none hover:-translate-y-1 hover:shadow-[8px_8px_0px_0px_hsl(var(--accent))] transition-all duration-300"
                >
                  Find Your Next Trip
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                {canFastTrack ? (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => navigate('/quiz?fastTrack=1')}
                    className="text-base sm:text-lg px-8 sm:px-10 py-5 sm:py-6 h-auto group bg-transparent text-primary-foreground border-primary-foreground/20 hover:bg-primary-foreground/10 rounded-none"
                  >
                    <Rocket className="mr-2 h-5 w-5" />
                    Continue as {personality!.archetype}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => document.getElementById('journey')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-base sm:text-lg px-8 sm:px-10 py-5 sm:py-6 h-auto group bg-transparent text-primary-foreground border-primary-foreground/20 hover:bg-primary-foreground/10 rounded-none"
                  >
                    How it works
                  </Button>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.6 }}
                className="mt-10 sm:mt-16 flex items-center gap-4 sm:gap-6"
              >
                <div className="flex -space-x-3">
                  {[
                    { initials: 'JD', bg: 'bg-accent' },
                      { initials: 'MK', bg: 'bg-secondary' },
                      { initials: 'AL', bg: 'bg-highlight' },
                  ].map((u, i) => (
                    <div
                      key={i}
                      className={cn(
                        'w-10 h-10 rounded-full border-2 border-primary flex items-center justify-center text-xs font-bold text-white',
                        u.bg
                      )}
                    >
                      {u.initials}
                    </div>
                  ))}
                </div>
                <p className="text-sm text-primary-foreground/50">
                  No endless tabs. Just a shortlist.
                </p>
              </motion.div>
            </motion.div>

            {/* Visual column — real destination cards */}
            <motion.div
              className="relative lg:col-span-5 w-full mt-4 lg:mt-0 h-[400px] sm:h-[520px] md:h-[560px] lg:h-[640px] flex items-center justify-center overflow-visible"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {/* Fixed design canvas, scaled to fit each breakpoint */}
              <div className="relative w-[360px] h-[540px] shrink-0 origin-center scale-[0.7] sm:scale-90 md:scale-100 lg:scale-[1.15]">
              {/* Lofoten Islands card */}
              <motion.div
                className="absolute top-0 right-0 w-[300px] bg-card rounded-xl overflow-hidden border border-border shadow-elevated z-30 transform rotate-3"
                whileHover={{ rotate: 0, scale: 1.02 }}
                transition={{ duration: 0.4 }}
              >
                {/* Banner */}
                <div className="relative h-40 w-full overflow-hidden">
                  <img
                    src="https://images.pexels.com/photos/3408744/pexels-photo-3408744.jpeg?auto=compress&cs=tinysrgb&w=600"
                    alt="Lofoten Islands destination"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
                  <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1">
                    <span className="h-1 w-3 rounded-full bg-white" />
                    <span className="h-1 w-1 rounded-full bg-white/60" />
                    <span className="h-1 w-1 rounded-full bg-white/60" />
                  </div>
                </div>

                <div className="px-4 pt-2.5 pb-3.5 space-y-2">
                  <h3 className="text-base font-semibold leading-tight flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 text-primary shrink-0" />
                    Lofoten Islands, Norway
                  </h3>

                  {/* Why we picked this */}
                  <div className="relative overflow-hidden rounded-md border border-success/25 bg-gradient-to-br from-success/10 via-success/5 to-transparent pl-2.5 pr-2 py-1.5">
                    <span aria-hidden className="absolute left-0 top-0 bottom-0 w-0.5 bg-success rounded-l-md" />
                    <div className="flex items-center gap-1 mb-1">
                      <Sparkles className="h-2.5 w-2.5 text-success" />
                      <span className="text-[8px] font-bold uppercase tracking-[0.14em] text-success">Why we picked this</span>
                    </div>
                    <ul className="space-y-0.5">
                      <li className="flex gap-1.5 text-[10px] leading-snug text-foreground">
                        <Check className="h-2.5 w-2.5 mt-0.5 shrink-0 text-success" strokeWidth={3} />
                        <span>Dramatic peaks and fjords for nature lovers.</span>
                      </li>
                      <li className="flex gap-1.5 text-[10px] leading-snug text-foreground">
                        <Check className="h-2.5 w-2.5 mt-0.5 shrink-0 text-success" strokeWidth={3} />
                        <span>Quiet villages far from the tourist rush.</span>
                      </li>
                    </ul>
                  </div>

                  {/* Activities */}
                  <div className="space-y-1">
                    <p className="text-[8px] uppercase tracking-wide text-highlight font-semibold">Tailored to you</p>
                    <div className="flex flex-wrap gap-1">
                      <span className="inline-flex items-center rounded border border-highlight/50 bg-highlight/15 text-highlight px-1.5 py-0.5 text-[9px] font-medium">Hiking</span>
                      <span className="inline-flex items-center rounded border border-highlight/50 bg-highlight/15 text-highlight px-1.5 py-0.5 text-[9px] font-medium">Photography</span>
                      <span className="inline-flex items-center rounded border border-highlight/50 bg-highlight/15 text-highlight px-1.5 py-0.5 text-[9px] font-medium">Wildlife</span>
                    </div>
                  </div>

                  {/* Budget */}
                  <div className="pt-1 border-t border-border">
                    <p className="text-[11px] font-semibold text-primary">Est. total: £1,200-£1,800 /person</p>
                  </div>
                </div>
              </motion.div>

              {/* Amalfi Coast card */}
              <motion.div
                className="absolute top-[196px] left-0 w-[300px] bg-card rounded-xl overflow-hidden border border-border shadow-elevated z-20 transform -rotate-6"
                whileHover={{ rotate: -2, scale: 1.02 }}
                transition={{ duration: 0.4 }}
              >
                {/* Banner */}
                <div className="relative h-40 w-full overflow-hidden">
                  <img
                    src="https://images.pexels.com/photos/351283/pexels-photo-351283.jpeg?auto=compress&cs=tinysrgb&w=600"
                    alt="Amalfi Coast destination"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
                  <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1">
                    <span className="h-1 w-3 rounded-full bg-white" />
                    <span className="h-1 w-1 rounded-full bg-white/60" />
                    <span className="h-1 w-1 rounded-full bg-white/60" />
                  </div>
                </div>

                <div className="px-4 pt-2.5 pb-3.5 space-y-2">
                  <h3 className="text-base font-semibold leading-tight flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 text-primary shrink-0" />
                    Amalfi Coast, Italy
                  </h3>

                  {/* Why we picked this */}
                  <div className="relative overflow-hidden rounded-md border border-success/25 bg-gradient-to-br from-success/10 via-success/5 to-transparent pl-2.5 pr-2 py-1.5">
                    <span aria-hidden className="absolute left-0 top-0 bottom-0 w-0.5 bg-success rounded-l-md" />
                    <div className="flex items-center gap-1 mb-1">
                      <Sparkles className="h-2.5 w-2.5 text-success" />
                      <span className="text-[8px] font-bold uppercase tracking-[0.14em] text-success">Why we picked this</span>
                    </div>
                    <ul className="space-y-0.5">
                      <li className="flex gap-1.5 text-[10px] leading-snug text-foreground">
                        <Check className="h-2.5 w-2.5 mt-0.5 shrink-0 text-success" strokeWidth={3} />
                        <span>Coastal drives and Italian food culture.</span>
                      </li>
                      <li className="flex gap-1.5 text-[10px] leading-snug text-foreground">
                        <Check className="h-2.5 w-2.5 mt-0.5 shrink-0 text-success" strokeWidth={3} />
                        <span>Ideal for relaxed couples and small groups.</span>
                      </li>
                    </ul>
                  </div>

                  {/* Activities */}
                  <div className="space-y-1">
                    <p className="text-[8px] uppercase tracking-wide text-highlight font-semibold">Tailored to you</p>
                    <div className="flex flex-wrap gap-1">
                      <span className="inline-flex items-center rounded border border-highlight/50 bg-highlight/15 text-highlight px-1.5 py-0.5 text-[9px] font-medium">Food tours</span>
                      <span className="inline-flex items-center rounded border border-highlight/50 bg-highlight/15 text-highlight px-1.5 py-0.5 text-[9px] font-medium">Boat trips</span>
                      <span className="inline-flex items-center rounded border border-highlight/50 bg-highlight/15 text-highlight px-1.5 py-0.5 text-[9px] font-medium">Coastal walks</span>
                    </div>
                  </div>

                  {/* Budget */}
                  <div className="pt-1 border-t border-border">
                    <p className="text-[11px] font-semibold text-primary">Est. total: £950-£1,450 /person</p>
                  </div>
                </div>
              </motion.div>


              {/* Architectural accent lines */}
              <div className="absolute top-1/2 left-0 w-full h-px bg-primary-foreground/5 -rotate-45 pointer-events-none" />
              <div className="absolute top-1/3 right-0 w-full h-px bg-accent/20 -rotate-12 pointer-events-none" />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Wave bridge into next section */}
        <div className="absolute -bottom-px left-0 right-0 pointer-events-none">
          <svg
            viewBox="0 0 1440 120"
            className="w-full h-16 sm:h-24 block"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              fill="hsl(var(--background))"
              d="M0,48 C360,108 720,0 1080,60 C1260,90 1380,84 1440,78 L1440,120 L0,120 Z"
            />
          </svg>
        </div>
      </section>


      {/* Built for the Undecided */}
      <section
        id="built-for"
        className="py-16 sm:py-24 relative"
      >
        <div className="absolute inset-0 -z-10 pointer-events-none bg-[radial-gradient(ellipse_at_15%_18%,hsl(var(--accent)/0.22),transparent_55%),radial-gradient(ellipse_at_82%_48%,hsl(var(--highlight)/0.24),transparent_58%)]" />

        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-highlight/10 border border-highlight/30 text-highlight text-xs font-semibold tracking-widest uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-highlight animate-pulse" />
                Built For The Undecided
              </span>
            </ScrollReveal>

            <ScrollReveal delay={0.05}>
              <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.1]">
                Ready to travel, but{" "}
                <span className="text-highlight">stuck deciding where?</span>
              </h2>
            </ScrollReveal>

            <ScrollReveal delay={0.1}>
              <p className="mt-4 text-muted-foreground text-lg sm:text-xl font-light leading-relaxed">
                You have the time, the budget and the urge to get away. TripVerse turns that energy into a decision you can act on, with everything you need in one place.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.15}>
              <div className="mt-4 flex flex-wrap justify-center gap-2.5">
                {[
                  "Beat decision paralysis",
                  "No more endless tabs",
                  "No more blog rabbit holes",
                  "No more group chat debates",
                  "Share & vote with a pitch deck",
                  "Unlock your Travel Personality",
                ].map((chip) => (
                  <span key={chip} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-card/80 border border-accent/30 text-sm font-medium text-foreground backdrop-blur-sm hover:border-accent/60 hover:-translate-y-0.5 transition-all duration-200">
                    <span className="flex items-center justify-center w-4 h-4 rounded-full bg-accent text-accent-foreground text-[10px] font-black">✓</span>
                    {chip}
                  </span>
                ))}
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <p className="mt-5 text-muted-foreground text-base sm:text-lg font-light leading-relaxed">
                Here is how TripVerse takes you from a rough idea to a booked trip, step by step.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>


      {/* Feature Journey */}
      <section
        id="journey"
        className="py-16 sm:py-24 relative"
      >
        <div className="absolute inset-0 -z-10 pointer-events-none bg-[radial-gradient(ellipse_at_80%_10%,hsl(var(--accent)/0.20),transparent_55%),radial-gradient(ellipse_at_10%_90%,hsl(var(--highlight)/0.18),transparent_55%)]" />

        <div className="container mx-auto px-4 sm:px-6">
          <ScrollReveal className="max-w-3xl mx-auto text-center mb-10 sm:mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-highlight/10 border border-highlight/30 text-highlight text-xs font-semibold tracking-widest uppercase mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              The Full Journey
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 leading-[1.1]">
              From a rough idea to{" "}
              <span className="text-highlight">a trip you've actually booked</span>
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg font-light">
              Eight steps, one flow. Take the quiz, build your shortlist, share it with the group, then book it and log it on your map.
            </p>
          </ScrollReveal>


          {[
            {
              img: featureQuiz,
              alt: "TripVerse quiz with trip purpose selection",
              tag: "Step 1 · The Quiz",
              title: "A few smart questions, that's it",
              desc: "Tell us where you're flying from, your budget, and the vibe you're after. Nine quick steps, no signup, and the AI builds a picture of your perfect trip.",
              points: ["Under two minutes to complete", "Climate, purpose, interests and pace", "Your picks visible at every step"],
            },
            {
              img: featureResults,
              alt: "Destination matches with live prices and a Replace button highlighted",
              tag: "Step 2 · Your Matches",
              title: "A multitude of recommendations at your fingertips",
              desc: "TripVerse surfaces a deep pool of destinations that match your quiz, then lets you explore them in focused batches. Every card shows live flight and hotel prices, a clear reason it fits, and the best months to go. Not feeling one? Tap Replace and we'll instantly pull in the next tailored option.",
              points: ["Live flight & hotel pricing", "One-tap Replace to shuffle any card", "Save your favourites as you go"],
            },
            {
              img: featureShortlist,
              alt: "Saved destinations dashboard with shortlisted cities",
              tag: "Step 3 · Shortlist",
              title: "Save the ones you love",
              desc: "Heart the picks that feel right, swap out the ones that don't, and come back to a clean shortlist whenever you're ready. No more lost tabs or screenshots buried in your camera roll.",
              points: ["Save unlimited destinations", "Swap suggestions on demand", "Pick up exactly where you left off"],
            },
            {
              img: featureShareDeck,
              alt: "Pitch deck of saved destinations with friend voting avatars",
              tag: "Step 4 · Share & Vote",
              title: "Turn your shortlist into a group decision",
              desc: "Stop the endless group chat. Share your saved destinations as a pitch deck so friends and family can browse, vote, and comment, then book the one everyone agrees on.",
              points: ["One link, no app required", "Vote for the favourite destination", "See comments and reach consensus fast"],
              featured: true,
            },
            {
              img: featureItinerary,
              alt: "Day by day itinerary timeline with activities",
              tag: "Step 5 · Itineraries",
              title: "See your trip, day by day",
              desc: "Generate a tailored itinerary for any saved destination, with daily plans, activities, and budgets that line up with the price you saw on the card.",
              points: ["Personalised daily plans", "Budget-aligned recommendations", "Edit and regenerate any day"],
            },
            {
              img: featureBooking,
              alt: "Booking dialog with curated flight, hotel and activity providers",
              tag: "Step 6 · Book It",
              title: "Book it in one click",
              desc: "When you're ready, we line up the best places to actually book: flights, hotels, tours and car hire, with smart tips for your destination and dates.",
              points: ["Hand-picked booking partners", "Destination-specific tips", "Trip summary auto-filled for you"],
            },
            {
              img: featureWorldmap,
              alt: "World map with visited countries highlighted",
              tag: "Step 7 · Your Map",
              title: "Watch your world map fill up",
              desc: "Log every place you've been and see your personal map light up country by country. A travel record that actually feels rewarding to keep.",
              points: ["Visual world map", "Countries and continents tracked", "A growing record of every trip"],
            },
            {
              img: featurePersonality,
              alt: "Travel Personality card showing archetype, radar chart, trait bars and clarity score",
              tag: "Step 8 · Unlockable",
              title: "Unlock your Travel Personality",
              desc: "The more you plan with TripVerse, the more we learn about how you travel. After a few quizzes and saves, you'll unlock your personal archetype: a living portrait of the traveller you're becoming. This feature is in beta, so expect it to keep improving the more you use it.",
              points: ["Unlocked from your quizzes, saves and visits", "Radar chart and clarity score that evolve with you", "One-tap Fast-Track: quiz skips to logistics, tailored to your archetype"],
              featured: true,
              beta: true,
            },
          ].map((f, i, arr) => (
            <ScrollReveal
              key={f.title}
              delay={0.1}
              direction={i % 2 === 0 ? "left" : "right"}
              className={`grid md:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto ${i < arr.length - 1 ? "mb-14 sm:mb-20" : ""}`}
            >
              <div className={`order-2 ${i % 2 === 1 ? "md:order-2" : "md:order-1"}`}>
                <motion.div
                  className="relative group"
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="absolute -inset-3 bg-gradient-to-br from-accent/20 via-highlight/15 to-transparent rounded-3xl blur-xl opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
                  <div className="relative rounded-2xl overflow-hidden border border-border shadow-soft bg-card">
                    <img
                      src={f.img}
                      alt={f.alt}
                      width={1280}
                      height={896}
                      loading="lazy"
                      className="w-full h-auto block transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                </motion.div>
              </div>
              <div className={`order-1 ${i % 2 === 1 ? "md:order-1" : "md:order-2"} space-y-4`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/30 text-accent text-xs font-semibold tracking-widest uppercase">
                    {f.tag}
                  </span>
                  {(f as any).featured && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-highlight text-highlight-foreground text-[10px] font-black tracking-widest uppercase shadow-sm">
                      ★ Crowd favourite
                    </span>
                  )}
                  {(f as any).beta && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-[10px] font-black tracking-widest uppercase shadow-sm">
                      BETA
                    </span>
                  )}
                </div>
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight leading-[1.15]">
                  {f.title}
                </h3>
                <p className="text-muted-foreground text-base sm:text-lg font-light leading-relaxed">
                  {f.desc}
                </p>
                <ul className="space-y-2 pt-1">
                  {f.points.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <span className="flex-shrink-0 mt-1 flex items-center justify-center w-5 h-5 rounded-full bg-highlight text-highlight-foreground text-xs font-black shadow-sm">✓</span>
                      <span className="text-foreground text-sm sm:text-base font-medium">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          ))}

        </div>
      </section>

      {/* Bottom CTA */}
      <section
        id="cta"
        className="relative bg-primary py-10 sm:py-14 overflow-hidden isolate"
      >
        <div className="absolute -top-px left-0 right-0 h-10 sm:h-12 bg-gradient-to-b from-background to-transparent pointer-events-none" />

        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-accent/10 blur-3xl"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-accent/8 blur-2xl"
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
          />
        </div>
        <ScrollReveal className="container mx-auto px-4 sm:px-6 relative z-10 text-center space-y-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary-foreground tracking-tight">
            Ready to find your next trip?
          </h2>
          <p className="text-primary-foreground/85 text-base sm:text-lg max-w-md mx-auto font-light">
            Skip the overwhelm. Find your next trip in 2 minutes.
          </p>
          <Button
            variant="hero"
            size="lg"
            onClick={() => navigate('/quiz')}
            className="text-base sm:text-lg px-10 sm:px-14 py-5 sm:py-7 h-auto group"
          >
            Get Started
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </ScrollReveal>
      </section>
    </div>
  );
};

export default Landing;
